import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CirclePlus, Loader2, Play, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogOverlay,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { staffService } from "@/services/staffService";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { useQuery } from "@tanstack/react-query";
import {
  useAutoExportSchedules,
  useCreateAutoExportSchedule,
  useDeleteAutoExportSchedule,
  useRunAutoExportNow,
  useUpdateAutoExportSchedule,
} from "../../../hooks/useAutoExportSchedules";
import { SearchableCheckboxDropdown } from "./SearchableCheckboxDropdown";
import { SearchableSelectDropdown } from "./SearchableSelectDropdown";
import {
  DEFAULT_EMAIL_DESCRIPTION,
  DEFAULT_EMAIL_TITLE,
  PERIOD_OPTIONS,
  REPORT_OPTIONS,
} from "./constants";
import {
  buildCreateScheduleBody,
  isReportTabAvailable,
  normalizeAnalyticsTab,
  scheduleToFormState,
  type AnalyticsReportTab,
} from "./autoExportMappers";
import {
  EMAIL_MODAL_ADD_EMAIL_BUTTON_CLASS,
  EMAIL_MODAL_CANCEL_BUTTON_CLASS,
  EMAIL_MODAL_SAVE_BUTTON_CLASS,
  EMAIL_MODAL_BODY_CLASS,
  EMAIL_MODAL_CLASS,
  EMAIL_MODAL_CLOSE_BUTTON_CLASS,
  EMAIL_MODAL_INPUT_CLASS,
  EMAIL_MODAL_LABEL_CLASS,
  EMAIL_MODAL_TITLE_CLASS,
} from "./styles";
import type { AutoExportSchedule } from "@/types/analytics-auto-export";

interface AutomaticEmailDistributionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hotelId?: string | null;
  isTenantAdmin?: boolean;
  defaultTab?: string;
  availableReportTabs?: ReadonlyArray<AnalyticsReportTab>;
}

function getStaffInitials(firstName?: string, lastName?: string): string {
  const first = firstName?.trim().charAt(0) ?? "";
  const last = lastName?.trim().charAt(0) ?? "";
  return `${first}${last}`.toUpperCase() || "?";
}

export const AutomaticEmailDistributionModal: React.FC<
  AutomaticEmailDistributionModalProps
> = ({
  open,
  onOpenChange,
  hotelId,
  isTenantAdmin = false,
  defaultTab = "overview",
  availableReportTabs,
}) => {
  const { t } = useTranslation();
  const { userProfile } = useAuthenticatedUser();
  const normalizedDefaultTab = normalizeAnalyticsTab(defaultTab);

  const [editingScheduleId, setEditingScheduleId] = useState<string | null>(
    null,
  );
  const [selectedReport, setSelectedReport] =
    useState<AnalyticsReportTab>(normalizedDefaultTab);
  const [selectedRecipientEmails, setSelectedRecipientEmails] = useState<
    string[]
  >([]);
  const [extraEmails, setExtraEmails] = useState<string[]>([]);
  const [newEmail, setNewEmail] = useState("");
  const [period, setPeriod] = useState("monthly");
  const [title, setTitle] = useState(DEFAULT_EMAIL_TITLE);
  const [description, setDescription] = useState(DEFAULT_EMAIL_DESCRIPTION);
  const [showEmailInput, setShowEmailInput] = useState(false);

  const listQuery = useAutoExportSchedules(
    {
      hotelId: isTenantAdmin ? undefined : (hotelId ?? undefined),
      skip: 0,
      take: 20,
    },
    open,
  );
  const createMutation = useCreateAutoExportSchedule();
  const updateMutation = useUpdateAutoExportSchedule();
  const deleteMutation = useDeleteAutoExportSchedule();
  const runNowMutation = useRunAutoExportNow();

  const staffQuery = useQuery({
    queryKey: ["analytics-auto-export-staff", hotelId],
    queryFn: () => staffService.getStaff({ hotelId: hotelId ?? undefined }),
    enabled: open && !!hotelId && !isTenantAdmin,
  });

  const isReportAllowed = useCallback(
    (tab: AnalyticsReportTab) => {
      return (
        isReportTabAvailable(tab, isTenantAdmin) &&
        (!availableReportTabs || availableReportTabs.includes(tab))
      );
    },
    [availableReportTabs, isTenantAdmin],
  );

  const availableReportOptions = useMemo(
    () => REPORT_OPTIONS.filter((option) => isReportAllowed(option.value)),
    [isReportAllowed],
  );

  const recipientOptions = useMemo(() => {
    const users = staffQuery.data?.users ?? [];
    return users
      .filter((user) => Boolean(user.email?.trim()))
      .map((user) => ({
        value: user.email!.trim(),
        label:
          [user.firstName, user.lastName].filter(Boolean).join(" ").trim() ||
          user.email!.trim(),
        subtitle: user.staff?.position,
        initials: getStaffInitials(user.firstName, user.lastName),
      }));
  }, [staffQuery.data?.users]);

  const resetForm = useCallback(() => {
    setEditingScheduleId(null);
    setSelectedReport(normalizedDefaultTab);
    setSelectedRecipientEmails([]);
    setExtraEmails([]);
    setNewEmail("");
    setPeriod("monthly");
    setTitle(DEFAULT_EMAIL_TITLE);
    setDescription(DEFAULT_EMAIL_DESCRIPTION);
    setShowEmailInput(false);
  }, [normalizedDefaultTab]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const tab = isReportAllowed(normalizedDefaultTab)
      ? normalizedDefaultTab
      : (availableReportOptions[0]?.value ?? "overview");
    setSelectedReport(tab);
  }, [open, normalizedDefaultTab, isReportAllowed, availableReportOptions]);

  const collectRecipients = useCallback((): string[] => {
    const emails = new Set<string>();

    selectedRecipientEmails.forEach((email) => {
      const trimmed = email.trim();
      if (trimmed) {
        emails.add(trimmed);
      }
    });

    extraEmails.forEach((email) => {
      const trimmed = email.trim();
      if (trimmed) {
        emails.add(trimmed);
      }
    });

    const fallbackEmail = userProfile?.email?.trim();
    if (emails.size === 0 && fallbackEmail) {
      emails.add(fallbackEmail);
    }

    return Array.from(emails);
  }, [extraEmails, selectedRecipientEmails, userProfile?.email]);

  const populateFromSchedule = useCallback(
    (schedule: AutoExportSchedule) => {
      const formState = scheduleToFormState(schedule, isTenantAdmin);
      if (!isReportAllowed(formState.reportTab)) {
        return;
      }

      setEditingScheduleId(schedule.id);
      setSelectedReport(formState.reportTab);
      setTitle(formState.title);
      setDescription(formState.description);
      setPeriod(formState.period);
      setExtraEmails(formState.extraEmails);
      setSelectedRecipientEmails([]);
      setShowEmailInput(false);
    },
    [isReportAllowed, isTenantAdmin],
  );

  const handleAddEmail = () => {
    const trimmed = newEmail.trim();
    if (!trimmed || extraEmails.includes(trimmed)) return;

    setExtraEmails((prev) => [...prev, trimmed]);
    setNewEmail("");
    setShowEmailInput(false);
  };

  const removeExtraEmail = (email: string) => {
    setExtraEmails((prev) => prev.filter((item) => item !== email));
  };

  const handleSave = async () => {
    const recipients = collectRecipients();

    if (recipients.length === 0) {
      toast.error(
        t(
          "analytics.emailDistribution.errors.noRecipients",
          "Add at least one recipient email",
        ),
      );
      return;
    }

    if (!isTenantAdmin && !hotelId) {
      toast.error(
        t(
          "analytics.emailDistribution.errors.noHotel",
          "Select a hotel before creating a schedule",
        ),
      );
      return;
    }

    if (!isReportAllowed(selectedReport)) {
      toast.error(
        t(
          "analytics.emailDistribution.errors.reportUnavailable",
          "This report is not available for the current subscription",
        ),
      );
      return;
    }

    const body = buildCreateScheduleBody({
      reportTab: selectedReport,
      isTenantAdmin,
      hotelId,
      title,
      description,
      recipients,
      period,
    });

    try {
      if (editingScheduleId) {
        await updateMutation.mutateAsync({ id: editingScheduleId, body });
        toast.success(
          t(
            "analytics.emailDistribution.success.updated",
            "Email schedule updated",
          ),
        );
      } else {
        await createMutation.mutateAsync(body);
        toast.success(
          t(
            "analytics.emailDistribution.success.created",
            "Email schedule created",
          ),
        );
      }
      onOpenChange(false);
      resetForm();
    } catch {
      toast.error(
        t(
          "analytics.emailDistribution.errors.saveFailed",
          "Failed to save email schedule",
        ),
      );
    }
  };

  const handleDelete = async (scheduleId: string) => {
    try {
      await deleteMutation.mutateAsync(scheduleId);
      if (editingScheduleId === scheduleId) {
        resetForm();
      }
      toast.success(
        t(
          "analytics.emailDistribution.success.deleted",
          "Email schedule deleted",
        ),
      );
    } catch {
      toast.error(
        t(
          "analytics.emailDistribution.errors.deleteFailed",
          "Failed to delete email schedule",
        ),
      );
    }
  };

  const handleRunNow = async (scheduleId: string) => {
    try {
      await runNowMutation.mutateAsync(scheduleId);
      toast.success(
        t(
          "analytics.emailDistribution.success.runQueued",
          "Export email queued for delivery",
        ),
      );
    } catch {
      toast.error(
        t(
          "analytics.emailDistribution.errors.runFailed",
          "Failed to queue export email",
        ),
      );
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const schedules = (listQuery.data?.data ?? []).filter((schedule) =>
    isReportAllowed(scheduleToFormState(schedule, isTenantAdmin).reportTab),
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          resetForm();
        }
        onOpenChange(nextOpen);
      }}
    >
      <DialogOverlay className="bg-black/50" />
      <DialogContent
        showCloseButton={false}
        className={cn(
          EMAIL_MODAL_CLASS,
          "border-base-border gap-0 rounded-2xl bg-white p-0",
        )}
      >
        <div className={EMAIL_MODAL_BODY_CLASS}>
          <div className="relative shrink-0">
            <DialogTitle className={EMAIL_MODAL_TITLE_CLASS}>
              {t(
                "analytics.emailDistribution.title",
                "Automatic email distribution",
              )}
            </DialogTitle>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className={EMAIL_MODAL_CLOSE_BUTTON_CLASS}
              aria-label={t("analytics.emailDistribution.close", "Close")}
            >
              <X className="size-5" />
            </button>
          </div>

          {schedules.length > 0 ? (
            <div className="flex flex-col gap-2">
              <Label className={EMAIL_MODAL_LABEL_CLASS}>
                {t(
                  "analytics.emailDistribution.existingSchedules",
                  "Existing schedules",
                )}
              </Label>
              <div className="max-h-36 space-y-2 overflow-y-auto">
                {schedules.map((schedule) => (
                  <div
                    key={schedule.id}
                    className="border-base-border flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm"
                  >
                    <button
                      type="button"
                      className="min-w-0 flex-1 truncate text-left hover:underline"
                      onClick={() => populateFromSchedule(schedule)}
                    >
                      {schedule.title}
                    </button>
                    <div className="flex shrink-0 items-center gap-1">
                      <button
                        type="button"
                        className="rounded p-1 text-black/45 hover:text-black/96"
                        aria-label={t(
                          "analytics.emailDistribution.runNow",
                          "Run now",
                        )}
                        onClick={() => handleRunNow(schedule.id)}
                        disabled={runNowMutation.isPending}
                      >
                        <Play className="size-4" />
                      </button>
                      <button
                        type="button"
                        className="rounded p-1 text-black/45 hover:text-red-600"
                        aria-label={t("analytics.common.delete", "Delete")}
                        onClick={() => handleDelete(schedule.id)}
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="size-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <SearchableSelectDropdown
            label={t(
              "analytics.emailDistribution.templatesLabel",
              "Templates/presets/report",
            )}
            placeholder={t(
              "analytics.emailDistribution.selectReport",
              "Select report",
            )}
            options={availableReportOptions}
            value={selectedReport}
            onChange={(value) =>
              setSelectedReport(normalizeAnalyticsTab(value))
            }
          />

          {recipientOptions.length > 0 ? (
            <SearchableCheckboxDropdown
              label={t("analytics.emailDistribution.sendTo", "Send to")}
              placeholder={t(
                "analytics.emailDistribution.selectUser",
                "Select a User",
              )}
              options={recipientOptions}
              value={selectedRecipientEmails}
              onChange={setSelectedRecipientEmails}
              showAvatar
            />
          ) : null}

          {extraEmails.map((email) => (
            <div
              key={email}
              className="border-base-border bg-secondary-badge flex items-center justify-between rounded-md border px-3 py-2 text-sm text-black/96"
            >
              <span className="truncate">{email}</span>
              <button
                type="button"
                onClick={() => removeExtraEmail(email)}
                className="text-black/45 hover:text-black/96"
                aria-label={t("analytics.emailDistribution.removeEmail", {
                  defaultValue: "Remove {{email}}",
                  email,
                })}
              >
                <X className="size-4" />
              </button>
            </div>
          ))}

          {showEmailInput ? (
            <div className="flex gap-3">
              <Input
                type="email"
                value={newEmail}
                onChange={(event) => setNewEmail(event.target.value)}
                placeholder={t(
                  "analytics.emailDistribution.emailPlaceholder",
                  "email@example.com",
                )}
                className={EMAIL_MODAL_INPUT_CLASS}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    handleAddEmail();
                  }
                }}
              />
              <Button
                type="button"
                variant="span"
                className={cn(EMAIL_MODAL_SAVE_BUTTON_CLASS, "h-10 shrink-0")}
                onClick={handleAddEmail}
              >
                {t("analytics.common.add", "Add")}
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              variant="span"
              className={EMAIL_MODAL_ADD_EMAIL_BUTTON_CLASS}
              onClick={() => setShowEmailInput(true)}
            >
              <CirclePlus className="size-4 shrink-0 text-[#0A0A0A]" />
              {t("analytics.emailDistribution.addEmail", "Add Email")}
            </Button>
          )}

          <div className="grid gap-3">
            <SearchableSelectDropdown
              label={t(
                "analytics.emailDistribution.frequency",
                "Delivery frequency",
              )}
              placeholder={t(
                "analytics.emailDistribution.selectFrequency",
                "Select frequency",
              )}
              options={[...PERIOD_OPTIONS]}
              value={period}
              onChange={setPeriod}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="email-title" className={EMAIL_MODAL_LABEL_CLASS}>
              {t("analytics.emailDistribution.titleField", "Title")}
            </Label>
            <Input
              id="email-title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              className={EMAIL_MODAL_INPUT_CLASS}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label
              htmlFor="email-description"
              className={EMAIL_MODAL_LABEL_CLASS}
            >
              {t("analytics.emailDistribution.description", "Description")}
            </Label>
            <Textarea
              id="email-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              className={`${EMAIL_MODAL_INPUT_CLASS} min-h-[120px] resize-none`}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="span"
              className={EMAIL_MODAL_CANCEL_BUTTON_CLASS}
              onClick={() => onOpenChange(false)}
              disabled={isSaving}
            >
              {t("analytics.common.cancel", "Cancel")}
            </Button>
            <Button
              type="button"
              variant="span"
              className={EMAIL_MODAL_SAVE_BUTTON_CLASS}
              onClick={handleSave}
              disabled={isSaving || !isReportAllowed(selectedReport)}
            >
              {isSaving ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                t("analytics.common.save", "Save")
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
